import Promise from 'bluebird';
import _ from 'lodash';
import * as sessionActionTypes from './sessionActionTypes';
import piction, {
  retrieveAndApplyUserConfig,
  setCookies,
  addSURL,
  addToken,
  isWorkbench,
  setLocale,
  getLocaleFromPictionIndex,
} from '../utils/piction';
import { CollectionItem } from '../utils/collectionUtils';
import {
  DEFAULT_COLLECTION_ID, LOCAL_STORAGE_CSRF_TOKEN,
  LOCAL_STORAGE_EXT_URL,
  LOCAL_STORAGE_EXTERNAL_LOGIN, LOCAL_STORAGE_LOCALE_KEY,
  LOCAL_STORAGE_SURL_KEY,
} from '../constants/session';
import appConfig from '../appConfig';
import { SESSION_EXPIRED_MESSAGE } from '../constants/sessionStateTypes';
import { addDisclaimerMessage } from './messageDialog';
import { saveState } from '../utils/localStorage';

const setLoginData = (surl, token, surlCookie, locale, isWB) => {
  saveState(LOCAL_STORAGE_CSRF_TOKEN, token);
  if (!surlCookie) {
    addSURL(surl);
    saveState(LOCAL_STORAGE_SURL_KEY, surl);
  }
  addToken(token);
  isWorkbench(isWB);
  setCookies(surlCookie);
  setLocale(locale);
};

export const validateSURLRequest = () => ({
  type: sessionActionTypes.VALIDATE_SURL_REQUEST,
});

export const validateSURLSuccess =
  (isValid,
   surl,
   token,
   surlCookie,
   username,
   isInvokeWorkbench = false,
   userCollection,
   extLogin = false,
   publicUsr,
    locale,
   ) => ({
     type: sessionActionTypes.VALIDATE_SURL_SUCCESS,
     isValid,
     surl,
     token,
     surlCookie,
     username,
     ...(isValid ? { isInvokeWorkbench } : {}),
     userCollection,
     extLogin,
     publicUsr,
     locale,
   });

export const validateSURLFailure = err => ({
  type: sessionActionTypes.VALIDATE_SURL_FAILURE,
  err,
});

export const validateSURL = (surl, token, surlCookie, isRefresh = false) =>
  function validateSURLDispatch(dispatch) {
    dispatch(validateSURLRequest());
    const externalLogin = sessionStorage.getItem(LOCAL_STORAGE_EXTERNAL_LOGIN);
    if (externalLogin) {
      sessionStorage.removeItem(LOCAL_STORAGE_EXT_URL);
      try {
        const loginData = JSON.parse(externalLogin);
        retrieveAndApplyUserConfig(loginData.SURL, loginData.CSRF, dispatch)
          .then(() => {
            setLoginData(loginData.SURL, loginData.CSRF,
              loginData.COOKIE_ENABLED === 'T' ? loginData.COOKIE_NAME : false,
              getLocaleFromPictionIndex(loginData.LNG_ID),
              loginData.ONLOGIN_INVOKE && loginData.ONLOGIN_INVOKE === 'WORKBENCH');
            dispatch(validateSURLSuccess(
              true,
              loginData.SURL,
              loginData.CSRF,
              loginData.COOKIE_ENABLED === 'T' ? loginData.COOKIE_NAME : false,
              loginData.USERNAME,
              loginData.ONLOGIN_INVOKE && loginData.ONLOGIN_INVOKE === 'WORKBENCH',
              loginData.AID,
            loginData.LOGIN_URL,
              loginData.PUBLIC || loginData.ACCOUNT_TYPE === "G",
              getLocaleFromPictionIndex(loginData.LNG_ID),
            ));
          });
        sessionStorage.removeItem(LOCAL_STORAGE_EXTERNAL_LOGIN);
        if (loginData.LOGIN_URL) sessionStorage.setItem(LOCAL_STORAGE_EXT_URL, loginData.LOGIN_URL);
        return (loginData);
      } catch (err) {
        dispatch(sessionExpired(err, true));
        return err;
      }
    } else if (surl || surlCookie || token) {
      // set base piction param
      if (surl) addSURL(surl);
      if (token) addToken(token);
      if (!_.isNull(surlCookie)) setCookies(surlCookie);

      return piction.validate()
        .then((res) => {
          if (res.isValid && isRefresh && appConfig.useUserDefinedConfig) {
            return retrieveAndApplyUserConfig(surl, token, dispatch)
              .then(() => res);
          }
          return res;
        })
        .then((res) => {
          setLoginData(res.surl,
            token,
            surlCookie || false,
            sessionStorage.getItem(LOCAL_STORAGE_LOCALE_KEY),
            res.res.a.on_login_invoke && res.res.a.on_login_invoke[0] === 'WORKBENCH');
          dispatch(validateSURLSuccess(
            res.isValid,
            res.surl,
            token,
            surlCookie,
            res.username,
            res.res.a.on_login_invoke && res.res.a.on_login_invoke[0] === 'WORKBENCH',
            res.res.a.a[0].$.aid,
            sessionStorage.getItem(LOCAL_STORAGE_EXT_URL),
            res.res.a.a[0].$.account_type === 'G',
            sessionStorage.getItem(LOCAL_STORAGE_LOCALE_KEY),
          ));
          return res;
        })
        .catch((err) => {
          dispatch(sessionExpired(err, true));
          return err;
        });
    }
    return Promise.reject(new Error())
      .catch((err) => {
        dispatch(sessionExpired(err, true));
        return err;
      });
  };

export const sessionExpiredFail = err => ({
  type: sessionActionTypes.SESSION_EXPIRED,
  err: new Error(SESSION_EXPIRED_MESSAGE),
});

export const sessionExpired = (err, isSURLFailed = false) =>
  function sessionExpiredDispatch(dispatch) {
    if (window.location.href.search('usr') !== -1 && appConfig.externalLogin) {
      window.location = appConfig.externalLogin.includes('http') ? appConfig.externalLogin :
        appConfig.cardURL + appConfig.externalLogin;
    } else if (isSURLFailed) dispatch(validateSURLFailure(err));
    else dispatch(sessionExpiredFail(err));
  };

export const validateSessionConnectionRequest = () => ({
  type: sessionActionTypes.VALIDATE_SESSION_CONNECTION_REQUEST,
});

export const validateSessionConnectionSuccess = () => ({
  type: sessionActionTypes.VALIDATE_SESSION_CONNECTION_SUCCESS,
});

export const validateSessionConnectionFailure = err => ({
  type: sessionActionTypes.VALIDATE_SESSION_CONNECTION_FAILURE,
  err,
});

export const validateSessionConnection = (surl, surlCookie) =>
  function validateSessionConnectionDispatch(dispatch) {
    if (!surl && surlCookie) {
      return Promise.reject(new Error())
        .catch((err) => {
          dispatch(sessionExpired(err));
          return err;
        });
    }
    setCookies(surlCookie);
    const promise = piction.validate(surl)
        .then((res) => {
          if (res.isValid) dispatch(validateSessionConnectionSuccess());
          else dispatch(sessionExpired(res));
          return res;
        })
        .catch((err) => {
          dispatch(sessionExpired(err));
          return err;
        });
    dispatch(validateSessionConnectionRequest());
    return promise;
  };

/* After receiving user config- update the store with the new values */
export const updateStoreFromNewConfig = () => ({
  type: sessionActionTypes.UPDATE_STORE_FROM_APPCONFIG,
});

export const loginRequest = () => ({
  type: sessionActionTypes.LOGIN_REQUEST,
});

export const loginSuccess = (
  surl,
  username,
  token,
  surlCookie,
  isInvokeWorkbench = false,
  userCollection,
  publicUsr,
  locale) => ({
    type: sessionActionTypes.LOGIN_SUCCESS,
    surl,
    username,
    token,
    surlCookie,
    isInvokeWorkbench,
    userCollection,
    publicUsr,
    locale,
  });

export const loginFailure = err => ({
  type: sessionActionTypes.LOGIN_FAILURE,
  err,
});

export const login = (username, password, isPublic) =>
  function loginDispatch(dispatch) {
    dispatch(loginRequest());

    // reset the environment
    // login through this function is done via kaui2
    // and not external url- so remove session storage param
    addSURL(null);
    sessionStorage.removeItem(LOCAL_STORAGE_EXT_URL);
    piction.setLanguage(undefined);
    return piction.login(username, password)
      .then((res) => {
        if (!res.csrf || _.isEmpty(res.csrf)) {
          console.error('KAUI2: No CSRF value. review your security configuration');
          throw new Error('Problem logging in. Please Review your configuration');
        }
        if (appConfig.useUserDefinedConfig) {
          return retrieveAndApplyUserConfig(res.surl, res.csrf, dispatch)
            .then(() => res);
        }
        return res;
      })
      .then((res) => {
        setLoginData(res.surl,
          res.csrf,
          res.surlCookie,
          getLocaleFromPictionIndex(res.res.a.LNG_ID[0]),
          res.res.a.ONLOGIN_INVOKE && res.res.a.ONLOGIN_INVOKE[0] === 'WORKBENCH');
        dispatch(loginSuccess(
          res.surl,
          username,
          res.csrf,
          res.surlCookie,
          res.res.a.ONLOGIN_INVOKE && res.res.a.ONLOGIN_INVOKE[0] === 'WORKBENCH',
          res.res.a.AID[0],
          res.res.a.ACCOUNT_TYPE[0] === 'G',
          getLocaleFromPictionIndex(res.res.a.LNG_ID[0]),
        ));
        if (appConfig.disclaimerMessage && appConfig.displayDisclaimer) {
          if (_.toUpper(appConfig.displayDisclaimer) === 'ALWAYS' || (_.toUpper(appConfig.displayDisclaimer) === 'ONCE' && res.res.a.LAST_LOGIN_TIME[0] === ''))
          dispatch(addDisclaimerMessage(appConfig.disclaimerMessage));
        }
        return res;
      })
      .catch((err) => {
        dispatch(loginFailure(isPublic ? { message: 'Please Login Again' } : err));
        return err;
      });
  };

export const logoutRequest = () => ({
  type: sessionActionTypes.LOGOUT_REQUEST,
});

export const logoutSuccess = () => ({
  type: sessionActionTypes.LOGOUT_SUCCESS,
});

export const logoutFailure = err => ({
  type: sessionActionTypes.LOGOUT_FAILURE,
  err,
});

export const logout = () =>
  function logoutDispatch(dispatch) {
    dispatch(logoutRequest());

    return piction.logout()
      .then((res) => {
        dispatch(logoutSuccess());
        return res;
      })
      .catch((err) => {
        dispatch(logoutFailure(err));
        return err;
      });
  };

export const retrieveCollectionsRequest = requestPromise => ({
  type: sessionActionTypes.RETRIEVE_COLLECTIONS_REQUEST,
  requestPromise,
});

export const retrieveCollectionsSuccess = collectionList => ({
  type: sessionActionTypes.RETRIEVE_COLLECTIONS_SUCCESS,
  collectionList,
});

export const retrieveCollectionsFailure = err => ({
  type: sessionActionTypes.RETRIEVE_COLLECTIONS_FAILURE,
  err,
});

export const retrieveCollections = () =>
  function retrieveCollectionsDispatch(dispatch) {
    const promise = piction.getCollections()
      .then((res) => {
        dispatch(retrieveCollectionsSuccess(
          res.res.a.rs ?
            res.res.a.rs.map(data => new CollectionItem(data.$.info, data._)) :
            []
        ));
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(retrieveCollectionsFailure(err));
        }
        return err;
      });

    dispatch(retrieveCollectionsRequest(promise));
    return promise;
  };

export const retrieveMetatagsRequest = (collectionID, requestPromise) => ({
  type: sessionActionTypes.RETRIEVE_METATAGS_REQUEST,
  requestPromise,
  collectionID,
});

export const retrieveMetatagsSuccess = (collectionID, metatagList) => ({
  type: sessionActionTypes.RETRIEVE_METATAGS_SUCCESS,
  metatagList,
  collectionID,
});

export const retrieveMetatagsFailure = (collectionID, err) => ({
  type: sessionActionTypes.RETRIEVE_METATAGS_FAILURE,
  err,
  collectionID,
});

export const retrieveMetatags = (collectionID = DEFAULT_COLLECTION_ID) =>
  function retrieveMetatagsDispatch(dispatch) {
    const promise = piction.getMetatags(
      collectionID !== DEFAULT_COLLECTION_ID ? { TAG_FOR_COLLECTION: collectionID } : { TAG_FOR_PROFILE_EDITED: '%' }
    )
      .then((res) => {
        dispatch(retrieveMetatagsSuccess(collectionID, res.res.a.META_TAG || []));
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          dispatch(sessionExpired(err));
        } else {
          dispatch(retrieveMetatagsFailure(collectionID, err));
        }
        return err;
      });
    dispatch(retrieveMetatagsRequest(collectionID, promise));
    return promise;
  };

export const logoutCompleted = () => ({
  type: sessionActionTypes.LOGOUT_COMPLETED,
});

export const changeLocale = locale => ({
  type: sessionActionTypes.CHANGE_LOCALE,
  locale,
});

export const updateDefaultCollectionLabel = label => ({
  type: sessionActionTypes.UPDATE_DEFAULT_COLLECTION_LABEL,
  label,
});



// WEBPACK FOOTER //
// ./src/js/actions/session.js